import React, { useEffect, useState } from 'react';
import { auth } from '../../firebase';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Phone, MessageSquare, CheckCircle2, ArrowRight, Loader2,
  ChevronLeft, PhoneCall, Repeat, AlertTriangle, Settings as SettingsIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

async function authFetch(path, opts = {}) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

const messagingLabel = (status) => ({
  active: 'Active',
  activating: 'Activating',
  approved: 'Approved',
  under_review: 'Under review',
  submitted: 'Under review',
  action_needed: 'Action needed',
  ready_to_submit: 'Ready to submit',
  rejected: 'Needs attention',
  setup_needed: 'Setup needed',
  inactive: 'Inactive',
}[status] || 'Setup needed');

// ── Business Number onboarding step ──────────────────────────────────────────
// Lightweight: choose a new CleanBid number OR check portability of an existing
// number. The full port / 10DLC intake lives in Settings → Business Number.
// This step NEVER blocks onboarding and NEVER reprovisions when a primary exists.
export default function BusinessNumberStep({ onContinue, onBack }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);       // business-number summary
  const [choice, setChoice] = useState(null);        // null | 'new' | 'keep'

  // new-number path
  const [provisioning, setProvisioning] = useState(false);
  const [newNumber, setNewNumber] = useState(null);

  // keep-number path
  const [keepPhone, setKeepPhone] = useState('');
  const [checking, setChecking] = useState(false);
  const [portResult, setPortResult] = useState(null); // {ok, portable, portRequest, message}

  useEffect(() => {
    (async () => {
      const { ok, data } = await authFetch('/api/telephony/business-number');
      if (ok) setStatus(data);
      setLoading(false);
    })();
  }, []);

  const getNewNumber = async () => {
    setProvisioning(true);
    const { ok, data } = await authFetch('/api/provisioning/assign-number', { method: 'POST' });
    setProvisioning(false);
    if (ok && data.phoneE164) setNewNumber(data.phoneE164);
  };

  const checkPortability = async () => {
    if (!keepPhone.trim()) return;
    setChecking(true);
    setPortResult(null);
    const { data } = await authFetch('/api/telephony/portability-check', {
      method: 'POST',
      body: JSON.stringify({ phone: keepPhone.trim() }),
    });
    setChecking(false);
    setPortResult(data);
  };

  const fmt = (e164) => {
    if (e164 && e164.startsWith('+1') && e164.length === 12) {
      return `(${e164.slice(2, 5)}) ${e164.slice(5, 8)}-${e164.slice(8)}`;
    }
    return e164;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-400 text-sm" data-testid="business-number-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your business number…
      </div>
    );
  }

  // ── Existing primary → show status, never offer to reprovision ──────────────
  if (status?.hasPrimary) {
    const p = status.primary;
    return (
      <div className="p-8 space-y-5" data-testid="business-number-existing">
        <StepHeader onBack={onBack} title="Your business number" subtitle="This is the number your customers reach you on." />
        <div className="bg-slate-900 rounded-xl p-5 border border-slate-700 space-y-4">
          <div className="text-2xl font-bold text-white tracking-tight" data-testid="business-number-value">
            {p.displayPhone || fmt(p.phoneE164)}
          </div>
          <div className="flex flex-col gap-2">
            <StatusRow icon={PhoneCall} label="Calling"
              value={p.calling.status === 'active' ? 'Active' : 'Inactive'}
              good={p.calling.status === 'active'} testid="calling-status" />
            <StatusRow icon={MessageSquare} label="Text messaging"
              value={messagingLabel(p.messaging.status)}
              good={p.messaging.status === 'active'} testid="messaging-status" />
          </div>
        </div>
        <a href="/settings/business-number"
          className="flex items-center justify-center gap-2 text-sm text-orange-400 hover:text-orange-300 transition-colors"
          data-testid="manage-business-number-link">
          <SettingsIcon className="w-4 h-4" /> Manage business number
        </a>
        <ContinueButton onClick={onContinue} testid="business-number-continue" />
      </div>
    );
  }

  // ── New-number success ──────────────────────────────────────────────────────
  if (newNumber) {
    return (
      <div className="p-8 space-y-5" data-testid="business-number-new-success">
        <StepHeader onBack={() => { setNewNumber(null); setChoice(null); }} title="Your new number is ready" subtitle="Customers can call and text this number." />
        <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-xl p-5 space-y-4">
          <div className="text-2xl font-bold text-white tracking-tight" data-testid="business-number-value">{fmt(newNumber)}</div>
          <StatusRow icon={PhoneCall} label="Calling" value="Ready" good testid="calling-status" />
          <StatusRow icon={MessageSquare} label="Text messaging" value="Setup needed" good={false} testid="messaging-status" />
        </div>
        <p className="text-slate-400 text-xs leading-relaxed">
          To start texting customers, finish a quick <strong className="text-slate-300">Text Messaging Setup</strong> anytime in
          {' '}<a href="/settings/business-number" className="text-orange-400 hover:text-orange-300">Settings → Business Number</a>. You can keep setting up CleanBid in the meantime.
        </p>
        <ContinueButton onClick={onContinue} testid="business-number-continue" />
      </div>
    );
  }

  // ── Keep-current-number: portability result ─────────────────────────────────
  if (portResult) {
    if (!portResult.ok) {
      return (
        <div className="p-8 space-y-5" data-testid="business-number-check-error">
          <StepHeader onBack={() => setPortResult(null)} title="We hit a snag" subtitle="" />
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-5 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-slate-300 text-sm">{portResult.message || "We couldn't check this number right now."}</p>
          </div>
          <Button onClick={checkPortability} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2.5" data-testid="business-number-retry">
            Try again
          </Button>
          <button onClick={onContinue} className="w-full text-xs text-slate-500 hover:text-slate-300 py-1" data-testid="business-number-skip">
            I'll do this later → Continue
          </button>
        </div>
      );
    }
    if (portResult.portable) {
      return (
        <div className="p-8 space-y-5" data-testid="business-number-portable">
          <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-xl p-5 flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" />
            <div>
              <p className="text-emerald-300 font-semibold">Good news — this number can be transferred to CleanBid.</p>
              <p className="text-slate-400 text-sm mt-1">{fmt(portResult.portRequest.phoneE164)}. We'll keep the number your customers already know.</p>
            </div>
          </div>
          <p className="text-slate-400 text-xs leading-relaxed">
            We'll gather the transfer details in <a href="/settings/business-number" className="text-orange-400 hover:text-orange-300">Settings → Business Number</a>. The transfer happens in the background — you can finish setting up CleanBid now.
          </p>
          <ContinueButton onClick={onContinue} testid="business-number-continue" />
        </div>
      );
    }
    return (
      <div className="p-8 space-y-5" data-testid="business-number-not-portable">
        <StepHeader onBack={() => setPortResult(null)} title="This number can't be transferred yet" subtitle="" />
        <div className="bg-slate-900 border border-slate-700 rounded-xl p-5">
          <p className="text-slate-300 text-sm">This number can't currently be transferred to CleanBid.</p>
          {portResult.portRequest?.notPortableReasonMessage
            && portResult.portRequest.notPortableReasonMessage !== "This number can't currently be transferred to CleanBid."
            && portResult.portRequest.notPortableReasonMessage !== "This number can’t currently be transferred to CleanBid." && (
            <p className="text-slate-500 text-xs mt-2" data-testid="not-portable-reason">
              {portResult.portRequest.notPortableReasonMessage}
            </p>
          )}
        </div>
        <Button onClick={() => { setPortResult(null); setChoice('new'); getNewNumber(); }}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2.5" data-testid="business-number-get-new-instead">
          Get a new CleanBid number instead
        </Button>
        <button onClick={() => setPortResult(null)} className="w-full text-xs text-slate-500 hover:text-slate-300 py-1">
          Try a different number
        </button>
      </div>
    );
  }

  // ── Keep-current-number: input ──────────────────────────────────────────────
  if (choice === 'keep') {
    return (
      <div className="p-8 space-y-5" data-testid="business-number-keep">
        <StepHeader onBack={() => setChoice(null)} title="Keep your current number" subtitle="Enter the business number your customers already know." />
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-sm font-medium">Current business number</Label>
          <Input data-testid="keep-number-input" placeholder="(555) 000-0000" value={keepPhone}
            onChange={(e) => setKeepPhone(e.target.value)}
            className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 h-11" autoFocus />
        </div>
        <Button onClick={checkPortability} disabled={checking || !keepPhone.trim()}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2.5" data-testid="check-portability-btn">
          {checking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Checking…</> : <>Check this number <ArrowRight className="ml-2 w-4 h-4" /></>}
        </Button>
      </div>
    );
  }

  // ── Choice ──────────────────────────────────────────────────────────────────
  return (
    <div className="p-8 space-y-5" data-testid="business-number-choice">
      <StepHeader onBack={onBack} title="Choose your business number" subtitle="How do you want customers to call and text you?" />
      <div className="space-y-3">
        <ChoiceCard
          testid="choice-new-number" icon={Phone} title="Get a new local CleanBid number"
          body="Start with a new local number for calls and text messaging."
          onClick={() => { setChoice('new'); getNewNumber(); }} busy={provisioning} />
        <ChoiceCard
          testid="choice-keep-number" icon={Repeat} title="Keep my current business number"
          body="Keep the number your customers already know. We'll transfer it to CleanBid without changing the number itself."
          onClick={() => setChoice('keep')} />
      </div>
      <button onClick={onContinue} className="w-full text-xs text-slate-600 hover:text-slate-400 py-1" data-testid="business-number-skip">
        I'll set this up later → Continue
      </button>
    </div>
  );
}

// ── Small presentational helpers ──────────────────────────────────────────────
function StepHeader({ title, subtitle, onBack }) {
  return (
    <div>
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-slate-400 hover:text-slate-200 text-sm mb-4 -ml-1 transition-colors">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
      )}
      <div className="flex items-center gap-2 mb-1">
        <Phone className="w-5 h-5 text-orange-400" />
        <h2 className="text-xl font-bold text-white">{title}</h2>
      </div>
      {subtitle ? <p className="text-slate-400 text-sm">{subtitle}</p> : null}
    </div>
  );
}

function ChoiceCard({ icon: Icon, title, body, onClick, busy, testid }) {
  return (
    <button onClick={onClick} disabled={busy} data-testid={testid}
      className="w-full text-left bg-slate-900 hover:bg-slate-900/70 border border-slate-700 hover:border-orange-500/50 rounded-xl p-4 transition-colors disabled:opacity-60">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-orange-500/15 flex items-center justify-center flex-shrink-0">
          {busy ? <Loader2 className="w-4 h-4 text-orange-400 animate-spin" /> : <Icon className="w-4 h-4 text-orange-400" />}
        </div>
        <div>
          <p className="text-white font-semibold text-sm">{title}</p>
          <p className="text-slate-400 text-xs mt-1 leading-relaxed">{body}</p>
        </div>
      </div>
    </button>
  );
}

function StatusRow({ icon: Icon, label, value, good, testid }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-slate-400 text-sm"><Icon className="w-4 h-4" /> {label}</span>
      <span className={`text-sm font-medium ${good ? 'text-emerald-400' : 'text-amber-400'}`} data-testid={testid}>{value}</span>
    </div>
  );
}

function ContinueButton({ onClick, testid }) {
  return (
    <Button onClick={onClick} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2.5" data-testid={testid}>
      Continue <ArrowRight className="ml-2 w-4 h-4" />
    </Button>
  );
}
